import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useLoginModal } from '@sparknz/journeys-common';
import {
  Box,
  SegmentedControl,
  Text,
  useSnackbar,
} from '@sparknz/spark-pattern-library';
import {
  ImpressionRegion,
  LinkRegion,
  trackCustomLink,
  useTriggerableTrackPageView,
} from '@sparknz/tracking';

import useProductEligibilityCheck from 'journey/productDetails/hooks/useProductEligibilityCheck';
import { useIdentity, useQuery, useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import { REVIEW_STEP, UPFRONT_PAYMENT_ID } from '../../constants/ifp.constants';
import useTrackPlanActionLayer from '../../hooks/useTrackPlanActionLayer.hook';
import { useProductDetails } from '../../provider';
import { ProductDetailsActionTypes } from '../../reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
  InfoLayerStatus,
} from '../../typings';
import {
  getProductDetailsPageViewParams,
  trackLinkedProductsEvent,
} from '../../utils';
import { updateStepSections } from '../../utils/ifp.utils';
import {
  BalanceManagementType,
  getPlansByBalanceManagementType,
} from '../../utils/plan.utils';
import { constructPlanCardImpressionData } from '../../utils/trackPlanActionLayerView.utils';
import ProductPlanCard from '../ProductConfigurator/components/ProductPlanCard';

const PLAN_SEGMENTS = [
  { label: 'Pay Monthly', value: BalanceManagementType.Postpaid },
  { label: 'Prepaid', value: BalanceManagementType.Prepaid },
];

const BUSINESS_PLAN_SEGMENTS = [
  { label: 'Pay Monthly', value: BalanceManagementType.Postpaid },
];

const Plans = () => {
  const router = useRouter();
  const ifpId = extractFirstValue(router.query.ifpId);
  const planId = extractFirstValue(router.query.planId);
  const { query, replaceQuery } = useQuery();
  const {
    dispatch,
    planOffers: planOffersDetails,
    ifpEligibility: {
      selectedLineNumber,
      sections,
      isCheckRequired,
      isChangePlan,
      ineligibleOfferIds,
    },
    selectedDevice,
    paymentOptions,
    changePlanOffers,
  } = useProductDetails();
  const { isBusiness } = useSegment();
  const { trackPageView } = useTriggerableTrackPageView();
  const { openLoginModal } = useLoginModal();
  const { showSnackbar } = useSnackbar();
  const { isCheckInProgress, fetchProductEligibility } =
    useProductEligibilityCheck();

  const { isLoggedIn, hasSparkId, refetchLoginStatus, refetchAccountList } =
    useIdentity();

  const isAuthenticatedSparkUser = isLoggedIn && hasSparkId;

  const [planSegment, setPlanSegment] = useState(PLAN_SEGMENTS[0].value);

  const planOffers = changePlanOffers || planOffersDetails;

  useTrackPlanActionLayer(planOffers);

  useEffect(() => {
    if (isCheckRequired) {
      trackLinkedProductsEvent({
        device: selectedDevice,
        eventInfo: {
          eventName: 'Mobile Plan Selector Plan View',
          eventAction: 'shop.mobilePlanSelector.plan.view',
        },
        plan: planOffers?.find(({ id }) => id === planId),
        paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
        isBusiness,
        isChangePlan,
      });
    }
  }, [isCheckRequired]);

  if (!planOffers) {
    return <Text variant="bodyMMedium">Plan options are not available.</Text>;
  }

  const isPayUpfront = ifpId === UPFRONT_PAYMENT_ID;

  const filteredPlanOffers = (
    isPayUpfront
      ? getPlansByBalanceManagementType(planOffers, planSegment)
      : planOffers
  ).filter(({ id }) => !ineligibleOfferIds?.includes(id));

  const handleInfoIconClick = (contentKey: string) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        contentKey,
        status: InfoLayerStatus.ContentFragment,
        isNested: true,
      },
    });
  };

  const segmentControlOptions = isBusiness
    ? BUSINESS_PLAN_SEGMENTS
    : PLAN_SEGMENTS;

  const isNewPlan = selectedLineNumber === 'NEW_PLAN';

  return (
    <>
      {isPayUpfront ? (
        <Box pt="s6">
          <SegmentedControl
            id="segmentedControl"
            label=""
            labelPosition="left"
            onChange={(selectedValue: BalanceManagementType) =>
              setPlanSegment(selectedValue)
            }
            options={segmentControlOptions}
            selectedValue={planSegment}
            variant="responsive"
          />
        </Box>
      ) : null}

      {filteredPlanOffers.map((offer) => {
        const isEligibilityCheckRequired =
          offer.productCharacteristics?.isEligibilityRequired;

        const impressionData = constructPlanCardImpressionData(offer);

        const selectPlan = async () => {
          await replaceQuery({ ...query, planId: offer.id });
          trackCustomLink('Select');
          dispatch({
            type: ProductDetailsActionTypes.SetActionLayer,
            actionLayerStatus: isNewPlan
              ? ActionLayerStatus.Close
              : ActionLayerStatus.ShowExistingPlan,
            ifpEligibility: {
              activeStep: isNewPlan
                ? IfpEligibilityCheckSteps.AddToCart
                : IfpEligibilityCheckSteps.ReviewPlan,
              selectedPlanTitle: offer.name,
              sections: updateStepSections([...sections, REVIEW_STEP]),
            },
            infoLayer: {
              status: InfoLayerStatus.Close,
            },
          });

          if (isNewPlan) {
            trackPageView(
              getProductDetailsPageViewParams({
                groupName: selectedDevice?.groupName,
                primaryCategory: selectedDevice?.primaryCategory,
              }),
            );
            trackLinkedProductsEvent({
              eventInfo: {
                eventName: 'Product View',
                eventAction: 'shop.prodViewAction',
              },
              device: selectedDevice,
              isBusiness,
              isChangePlan,
            });
          }
        };

        const handleEligibilityCheck = async () => {
          const { isEligible } = await fetchProductEligibility(offer.id);

          if (isEligible) {
            await selectPlan();

            showSnackbar({
              text: 'You are eligible for this plan.',
            });

            return;
          }

          dispatch({
            type: ProductDetailsActionTypes.HandleIneligiblePlan,
            ineligibleOfferId: offer.id,
          });
        };

        const handlePlanSelect = async () => {
          if (!isEligibilityCheckRequired) {
            await selectPlan();
            return;
          }

          if (isAuthenticatedSparkUser) {
            await handleEligibilityCheck();
            return;
          }

          openLoginModal({
            onLoginSuccess: async () => {
              refetchLoginStatus();
              refetchAccountList();

              await handleEligibilityCheck();
            },
          });
        };

        return (
          <Box key={offer.id} pt={['s2', 's3', 's4']}>
            <LinkRegion regionId={`productDetails.plan.${offer.name}`}>
              <ImpressionRegion threshold={0.7} impressionData={impressionData}>
                <ProductPlanCard
                  offer={offer}
                  selectedPlanId={planId}
                  isAuthenticatedSparkUser={isAuthenticatedSparkUser}
                  isEligibilityCheckRequired={isEligibilityCheckRequired}
                  isCheckInProgress={isCheckInProgress}
                  handlePlanSelect={handlePlanSelect}
                  handleInfoIconClick={handleInfoIconClick}
                />
              </ImpressionRegion>
            </LinkRegion>
          </Box>
        );
      })}
    </>
  );
};

export default Plans;
